import {EventSourcePolyfill} from 'event-source-polyfill';
import baseUrl from './baseUrl';
import http, {pickData, pickList, ensureFreshToken, getStoredToken} from './http';
import {FILE_BASE, ADMIN_FILE_CATEGORY_BASE} from './paths';
import {withPerm} from './perm';

const extractCategoryList = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.content)) {
    return payload.content;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const listFileCategories = withPerm(async (options = {}) => {
  const params = {
    page: options.page ?? 0,
    size: options.size ?? 200,
  };
  if (options.keyword) {
    params.keyword = options.keyword;
  }

  let firstError = null;

  try {
    const response = await http.get(`${ADMIN_FILE_CATEGORY_BASE}`, { params });
    const data = pickData(response);
    const list = extractCategoryList(data);
    if (Array.isArray(list) && list.length >= 0) {
      return list;
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 404 && status !== 405 && status !== 501) {
      throw error;
    }
    firstError = error;
  }

  try {
    const response = await http.get(`${ADMIN_FILE_CATEGORY_BASE}/all`);
    return pickList(response);
  } catch (fallbackError) {
    if (firstError) {
      throw firstError;
    }
    throw fallbackError;
  }
}, 'GET', `${ADMIN_FILE_CATEGORY_BASE}`);

export const getFileCategory = withPerm(async (categoryId) => {
  const response = await http.get(`${ADMIN_FILE_CATEGORY_BASE}/${categoryId}`);
  return pickData(response);
}, 'GET', `${ADMIN_FILE_CATEGORY_BASE}/{categoryId}`);

export const createFileCategory = withPerm(async (payload) => {
  const response = await http.post(`${ADMIN_FILE_CATEGORY_BASE}`, payload);
  return pickData(response);
}, 'POST', `${ADMIN_FILE_CATEGORY_BASE}`);

export const updateFileCategory = withPerm(async (categoryId, payload) => {
  const response = await http.put(`${ADMIN_FILE_CATEGORY_BASE}/${categoryId}`, payload);
  return pickData(response);
}, 'PUT', `${ADMIN_FILE_CATEGORY_BASE}/{categoryId}`);

export const deleteFileCategory = withPerm(async (categoryId) => {
  const response = await http.delete(`${ADMIN_FILE_CATEGORY_BASE}/${categoryId}`);
  return pickData(response);
}, 'DELETE', `${ADMIN_FILE_CATEGORY_BASE}/{categoryId}`);

export const getFileCategoryByCode = withPerm(async (categoryCode) => {
  const response = await http.get(`${FILE_BASE}/categories/code/${categoryCode}`);
  return pickData(response);
}, 'GET', `${FILE_BASE}/categories/code/{categoryCode}`);

export const listFileGroups = withPerm(async (categoryId) => {
  const response = await http.get(`${FILE_BASE}/categories/${categoryId}/groups`);
  return pickList(response);
}, 'GET', `${FILE_BASE}/categories/{categoryId}/groups`);

export const createFileGroup = withPerm(async (categoryId, payload) => {
  const response = await http.post(`${FILE_BASE}/categories/${categoryId}/groups`, payload);
  return pickData(response);
}, 'POST', `${FILE_BASE}/categories/{categoryId}/groups`);

export const updateFileGroup = withPerm(async (groupId, payload) => {
  const response = await http.put(`${FILE_BASE}/groups/${groupId}`, payload);
  return pickData(response);
}, 'PUT', `${FILE_BASE}/groups/{groupId}`);

export const deleteFileGroup = withPerm(async (groupId) => {
  const response = await http.delete(`${FILE_BASE}/groups/${groupId}`);
  return pickData(response);
}, 'DELETE', `${FILE_BASE}/groups/{groupId}`);

export const getFileGroup = withPerm(async (groupId) => {
  const response = await http.get(`${FILE_BASE}/groups/${groupId}`);
  return pickData(response);
}, 'GET', `${FILE_BASE}/groups/{groupId}`);

export const listManagedFiles = withPerm(async (groupId) => {
  const response = await http.get(`${FILE_BASE}/groups/${groupId}/files`);
  return pickList(response);
}, 'GET', `${FILE_BASE}/groups/{groupId}/files`);

export const uploadFilesToCategory = withPerm(async (categoryId, files, {
  descriptions,
  groupName,
  groupDescription,
  uploadId,
} = {}) => {
  if (!categoryId) {
    throw new Error('카테고리 ID가 필요합니다.');
  }
  if (!files || files.length === 0) {
    throw new Error('업로드할 파일을 선택하세요.');
  }
  const formData = new FormData();
  formData.append('categoryId', categoryId);
  if (groupName) {
    formData.append('groupName', groupName);
  }
  if (groupDescription) {
    formData.append('groupDescription', groupDescription);
  }
  if (uploadId) {
    formData.append('uploadId', uploadId);
  }
  const descList = Array.isArray(descriptions) ? descriptions : [];
  const supportsFile = typeof File !== 'undefined';
  Array.from(files).forEach((file, index) => {
    const isFileLike = (supportsFile && file instanceof File) || file instanceof Blob;
    if (isFileLike) {
      const filename = file.name || `file-${index}`;
      formData.append('files', file, filename);
      if (descList[index]) {
        formData.append('descriptions', descList[index]);
      }
    }
  });
  const response = await http.post(`${FILE_BASE}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return pickData(response);
}, 'POST', `${FILE_BASE}/upload`);

export const uploadManagedFile = withPerm(async (groupId, formData, config = {}) => {
  const response = await http.post(`${FILE_BASE}/group/${groupId}/add`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    ...config,
  });
  return pickData(response);
}, 'POST', `${FILE_BASE}/group/{groupId}/add`);

export const deleteManagedFile = withPerm(async (fileId) => {
  const response = await http.delete(`${FILE_BASE}/${fileId}`);
  return pickData(response);
}, 'DELETE', `${FILE_BASE}/{fileId}`);

const decodeFileNameComponent = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch (_) {
    return value;
  }
};

const extractFileNameFromDisposition = (headerValue) => {
  if (!headerValue || typeof headerValue !== 'string') {
    return null;
  }
  const starMatch = headerValue.match(/filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i);
  if (starMatch && starMatch[1]) {
    return decodeFileNameComponent(starMatch[1].replace(/^"|"$/g, ''));
  }
  const filenameMatch = headerValue.match(/filename\s*=\s*"?([^";]+)"?/i);
  if (filenameMatch && filenameMatch[1]) {
    return decodeFileNameComponent(filenameMatch[1].trim());
  }
  return null;
};

const normalizeDownloadResponse = (response) => {
  if (!response) {
    return { blob: null, fileName: null, contentType: null };
  }
  const headers = response.headers || {};
  return {
    blob: response.data ?? null,
    fileName: extractFileNameFromDisposition(headers['content-disposition']) || null,
    contentType: headers['content-type'] || null,
  };
};

export const downloadManagedFileById = withPerm(async (fileId) => {
  if (!fileId) {
    throw new Error('파일 ID가 필요합니다.');
  }
  const response = await http.get(`${FILE_BASE}/${fileId}/download`, {
    responseType: 'blob',
    __silent: true,
  });
  return normalizeDownloadResponse(response);
}, 'GET', `${FILE_BASE}/{fileId}/download`);

export const downloadFileByUrl = async (downloadUrl) => {
  if (!downloadUrl) {
    throw new Error('다운로드 URL이 필요합니다.');
  }
  const response = await http.get(downloadUrl, {
    responseType: 'blob',
    __silent: true,
  });
  return normalizeDownloadResponse(response);
};

const parseSsePayload = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const buildAuthHeader = (tokenObj) => {
  if (!tokenObj?.token) return null;
  const prefix = tokenObj.tokenType || 'Bearer';
  return `${prefix} ${tokenObj.token}`;
};

export const subscribeUploadProgress = async (uploadId, {
  withCredentials = false,
  onEvent,
  onError,
} = {}) => {
  const ensured = await ensureFreshToken().catch(() => null);
  const tokenObj = ensured || getStoredToken();
  const headers = {};
  const authValue = buildAuthHeader(tokenObj);
  if (authValue) {
    headers.Authorization = authValue;
  }
  const origin = baseUrl || '';
  const url = `${origin}${FILE_BASE}/uploads/${uploadId}/progress`;
  const eventSource = new EventSourcePolyfill(url, {
    headers,
    withCredentials,
  });

  const emit = (type) => (event) => {
    if (!onEvent) return;
    const payload = parseSsePayload(event?.data);
    onEvent(type, payload);
  };

  eventSource.addEventListener('STARTED', emit('STARTED'));
  eventSource.addEventListener('IN_PROGRESS', emit('IN_PROGRESS'));
  eventSource.addEventListener('COMPLETED', emit('COMPLETED'));
  eventSource.addEventListener('FAILED', emit('FAILED'));

  if (onError) {
    eventSource.onerror = (event) => {
      onError(event);
    };
  }

  return eventSource;
};
